import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { LEAGUE_LABELS } from '@pickleballinc/lib/product-labels';
import clsx from 'clsx';
import { uniqueId } from 'lodash';

import { publicEnv } from '@/lib/env';
import { EmptyMascotSize } from '@/modules/players/v3/types/dashboard';

export const BLANK_IMAGE = getCDNURL(
  `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/placeholder/pickleball_transparent_square.png`,
  128,
  128
);

export const SHUTTERSTOCK_COLORED = getCDNURL(
  `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/other/shutterstock-colored.png`,
  642,
  292
);

export const DEFAULT_PROFILE_IMAGE_URL = `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/pickleball-app/static/profile-image.svg`;

/** Disabled everywhere until the join/checkout flow ships — see PICL-197. */
export const JOIN_PB_PLUS_LABEL = formatText({
  text: 'Join Pickleball+',
  role: TextRole.ButtonLabel,
});

export const PICKLEBALL_CENTRAL_URL = 'https://pickleballcentral.com';
export const PICKLEBALL_CENTRAL_LOGO = getCDNURL(
  `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/outside/PICKLEBALL_CENTRAL_LOGO.svg`,
  120,
  35
);
export const PLAYER_GEAR_FAVORITES_MODULE_FOR = 175;
export const GLOBAL_SYSTEM_MODULE_TYPE = 109;

export const DEFAULT_WPR_RANKINGS_WOMENS_DIVISION_TYPE = '4';
export const DEFAULT_WPR_RANKINGS_MENS_DIVISION_TYPE = '5';

const PPA_CLUB_ID = '80f17003-cd92-4fc4-84e4-06d38e096042';
const WPT_CLUB_ID = '211EBD1B-E2AD-4385-A3FC-F13E6A63F206';
const APA_CLUB_ID = '1BB5E4C0-99D6-4718-A73B-5A2AD890CD7E';
const PICKLR_CLUB_ID = '33E5B0FA-3494-4052-87EE-72322AE47BA2';
const PPA_AUSTRALIA_CLUB_ID = '172c0d07-c2da-47e8-b45a-1bfe0c347f2d';
const THE_FORT_CLUB_ID = 'ce346991-9802-4789-99f9-7c6f37236d57';

export {
  PPA_CLUB_ID,
  WPT_CLUB_ID,
  APA_CLUB_ID,
  PICKLR_CLUB_ID,
  PPA_AUSTRALIA_CLUB_ID,
  THE_FORT_CLUB_ID,
};

export const MY_GEAR_ALL_BRAND_OPTION = { id: 'all', name: 'All' };

export const DASHBOARD_WIDGET_CAROUSEL_DEFAULT_ITEMS_PER_PAGE = 2;

/** Below this container width the carousel uses its mobile item count, whatever the viewport. */
export const DASHBOARD_WIDGET_CAROUSEL_NARROW_WIDTH_PX = 420;

export const DRAG_CLICK_SUPPRESS_PX = 10;

export const emptyStateMascotImageHover = (position: 'bottom-left' | 'bottom-right') =>
  clsx(
    position === 'bottom-left' ? 'origin-bottom-left' : 'origin-bottom-right',
    'md:group-hover:animate-mascot-wobble',
    'motion-reduce:md:group-hover:animate-none'
  );

export const emptyStateIconImageHover = clsx(
  'origin-center transition-transform duration-300 ease-out',
  'md:group-hover:-translate-y-1 md:group-hover:scale-[1.08] md:group-hover:rotate-[3deg]',
  'motion-reduce:transition-none motion-reduce:md:group-hover:translate-y-0 motion-reduce:md:group-hover:scale-100 motion-reduce:md:group-hover:rotate-0'
);

export const MASCOT_SLOT: Record<EmptyMascotSize, string> = {
  compact: 'h-[104px] w-[125px]',
  default: 'h-[170px] w-[132px]',
  spacious: 'h-[220px] w-[160px]',
};

export const EventLabel = {
  tournament: 'Tournament',
  league: LEAGUE_LABELS.plural,
  ladder_league: LEAGUE_LABELS.plural,
  team_league: 'Team League',
  club: 'Clubs',
  pickup_game: 'Game Maker',
} as const;

export const PICKLEBALL_RANKING_DIVISION_TYPE = {
  WOMENS_DOUBLES: '4',
  MENS_DOUBLES: '5',
  MIXED: '3',
  WOMENS_SINGLES: '1',
  MENS_SINGLES: '2',
  WOMENS_MIXED: '7',
  MENS_MIXED: '6',
} as const;

export const PICKLEBALL_RANKING_PROFILE_DIVISION_TYPES = [
  {
    id: uniqueId(),
    title: 'Doubles',
    value: 'Doubles',
    femaleType: '4',
    maleType: '5',
  },
  {
    id: uniqueId(),
    title: 'Mixed',
    value: 'Mixed',
    femaleType: '3',
    maleType: '3',
  },
  {
    id: uniqueId(),
    title: 'Singles',
    value: 'Singles',
    femaleType: '1',
    maleType: '2',
  },
];

export const SUBSCRIPTIONS_TABLE_COLUMNS = [
  { key: 'name', label: 'Name', className: 'py-2' },
  { key: 'organization', label: 'Organization' },
  { key: 'fees', label: 'Fees' },
  { key: 'startDate', label: 'Start Date' },
  { key: 'endDate', label: 'End Date' },
  {
    key: 'status',
    label: 'Status',
    className: 'sticky right-[140px] bg-white z-10 text-center',
  },
  {
    key: 'actions',
    label: 'Actions',
    className: 'w-[140px] sticky right-0 bg-white z-10 text-center',
  },
];

export const LOGIN_HISTORY_TABLE_COLUMNS = [
  { key: 'date', label: 'Date', className: 'py-2 pl-4' },
  { key: 'location', label: 'Location' },
  { key: 'device', label: 'Device', className: 'text-center' },
  { key: 'deviceType', label: 'Device Type' },
  { key: 'deviceModel', label: 'Device Model' },
  { key: 'browser', label: 'Browser' },
  { key: 'browserVersion', label: 'Browser Version', className: 'text-center' },
  { key: 'app', label: 'App', className: 'text-center' },
  { key: 'impersonator', label: 'Impersonator', className: 'pr-4' },
];

export const PASSWORD_HISTORY_TABLE_COLUMNS = [
  { key: 'date', label: 'Date', className: 'py-2 pl-4' },
  { key: 'location', label: 'Location' },
  { key: 'deviceType', label: 'Device Type' },
  { key: 'browser', label: 'Browser' },
  { key: 'browserVersion', label: 'Browser Version', className: 'text-center' },
  { key: 'device', label: 'Device', className: 'text-center' },
  { key: 'changedBy', label: 'Changed By' },
];

export const APP_ACCESS_TABLE_COLUMNS = [
  { key: 'name', label: 'Name', className: 'py-2 pl-4 pr-4' },
  { key: 'status', label: 'Current Status', className: 'px-4' },
  { key: 'access', label: 'Revoke / Grant Access', className: 'px-4 text-center' },
];
